import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCustomerTickets } from '../../api/customer';
import { getLoyaltyBalance } from '../../api/loyalty';
import { getPublicDrawsList, getDrawInfoById } from '../../api/draws';
import SecureStorage from '../../utils/secureStorage';
import { LoyaltyBalance } from '../../@types/loyalty';
import { PATHS } from '../../routes/routes';
import { format } from 'date-fns';
// import { getDrawInfoById } from '../../api/draws';
import confetti from 'canvas-confetti';

import CustomerTopNav from '../../components/customerPortal/CustomerTopNav';
import CustomerFooter from '../../components/customerPortal/CustomerFooter';
import '../../styles/customerPortal.css';

// Helper function to decode email from customer token
function decodeEmailFromToken(bearerToken: string | null): string | null {
    if (!bearerToken) return null;
    const token = bearerToken.startsWith('Bearer ') ? bearerToken.slice(7) : bearerToken;
    const parts = token.split('.');
    if (parts.length < 2) return null;
    try {
        const payload = JSON.parse(atob(parts[1]));
        const emailRaw = payload?.email ?? payload?.sub ?? null;
        return emailRaw ? String(emailRaw).trim().toLowerCase() : null;
    } catch {
        return null;
    }
}

interface Ticket {
    id: string;
    ticketCode: string;
    ticketFor: 'DRAW' | 'MEMBER' | 'PROMO';
    runDate?: string;
    membershipTirePaymentId?: string;
    membershipTireId?: string;
    drawID: string;
    generatedCode: string;
    hasWon: boolean;
    ownerEmail: string;
    paymentId: string;
    paymentStatus?: string;
    cancelledAt?: string;
    createdAt: string;
    updatedAt: string;
    deactivatedAt: string | null;
    // Membership details
    leftRecurrence?: number;
    recurring?: boolean;
    numberOfTicket?: number;
    status?: string;
    tier?: {
        id: string;
        name: string;
        frequency: string;
        price: number;
        currency: string;
        recurringEntry: boolean;
        numberOfTicket: number;
    };
    // New ticket breakdown fields
    ticketBreakdown?: {
        bought: number;
        promotional: number;
        total: number;
    };
    paymentAmount?: number;
    paymentCurrency?: string;
    purchaseDate?: string;
}

interface Draw {
    id: string;
    name: string;
    prize: string;
    entryCost: string;
    runAt: string;
    runMethod: string;
    currency: string;
    timezone: string;
    imageId?: string;
    noOfPartic: number;
    discounts?: Array<{ type: string; code: string }>;
    createdAt: string;
    updatedAt: string;
}

const CustomerPortal = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [loyaltyBalance, setLoyaltyBalance] = useState<LoyaltyBalance | null>(null);
    const [activeTab, setActiveTab] = useState<'tickets' | 'memberships' | 'draws' | 'past' | 'pastTickets'>('tickets');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;
    const [tickets, setTickets] = useState<{
        memberships: Ticket[];
        upcomingTickets: Ticket[];
        pastTickets: Ticket[];
    }>({
        memberships: [],
        upcomingTickets: [],
        pastTickets: []
    });
    
    const [draws, setDraws] = useState<{
        upcoming: Draw[];
        past: Draw[];
    }>({
        upcoming: [],
        past: []
    });
    const [openTicketGroups, setOpenTicketGroups] = useState<Record<string, boolean>>({});
    // Discounts moved to dedicated Perks page

    // Get customer email from token
    const customerEmail = useMemo(() => decodeEmailFromToken(SecureStorage.getCustomerToken()), []);

    useEffect(() => {
        console.log('📱 CustomerPortal: Initializing...');
        // Authentication is now handled by CustomerGuard, so just load data
        console.log('✅ Loading portal data...');
        fetchTickets();
        fetchDraws();
        fetchLoyaltyBalance();
    }, []);

    // Reset page when changing tabs
    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab]);

    const fetchTickets = async () => {
        try {
            setLoading(true);
            if (!SecureStorage.isCustomerTokenValid()) {
                console.log('No valid token for fetching tickets');
                navigate(PATHS.CUSTOMER_SIGNIN);
                return;
            }
            const response = await getCustomerTickets();
            console.log('DEBUG: Raw tickets response:', response.data);
            console.log('DEBUG: Memberships count:', response.data.memberships?.length || 0);
            console.log('DEBUG: Memberships data:', response.data.memberships);
            setTickets(response.data);

            // Build upcoming draws only for this customer (based on their tickets)
            const upcomingTicketDrawIds: string[] = Array.from(
                new Set(
                    (response.data.upcomingTickets || [])
                        .map((t: any) => t.drawID)
                        .filter((id: any) => !!id)
                )
            );

            if (upcomingTicketDrawIds.length > 0) {
                try {
                    const details = await Promise.all(
                        upcomingTicketDrawIds.map(async (id) => {
                            const resp = await getDrawInfoById(id);
                            const raw = resp?.data?.draw || resp?.data?.data?.draw || resp?.data?.data || resp?.data;
                            return raw as Draw;
                        })
                    );
                    setDraws((prev) => ({ ...prev, upcoming: details }));
                } catch (e) {
                    console.warn('Failed to load customer upcoming draw details', e);
                }
            } else {
                // If no customer upcoming, clear upcoming list
                setDraws((prev) => ({ ...prev, upcoming: [] }));
            }
        } catch (error: any) {
            if (error.response?.status === 401) {
                SecureStorage.removeCustomerToken();
                console.log('Token expired, redirecting to signin');
                navigate(PATHS.CUSTOMER_SIGNIN);
            }
            setError(error.response?.data?.error || 'Failed to fetch tickets');
        } finally {
            setLoading(false);
        }
    };

    const fetchDraws = async () => {
        try {
            console.log('🎯 Fetching draws...');
            const response = await getPublicDrawsList({ page: 1, pageSize: 100 });
            const allDraws = response.data.draws || [];
            
            console.log('📊 All draws:', allDraws);
            
            // Filter draws by date
            const now = new Date();
            // Do not use public upcoming draws for the tab; upcoming tab is customer-only
            const upcomingDraws: Draw[] = [];
            const pastDraws = allDraws.filter((draw: Draw) => 
                new Date(draw.runAt) <= now && parseFloat(draw.entryCost) > 0 // Only regular draws, not promo
            );
            
            console.log('🚀 Upcoming draws:', upcomingDraws);
            console.log('📅 Past draws:', pastDraws);
            
            setDraws((prev) => ({
                upcoming: prev.upcoming, // preserve customer-only upcoming list populated by fetchTickets
                past: pastDraws
            }));
        } catch (error: any) {
            console.error('Error fetching draws:', error);
            setError(error.response?.data?.error || 'Failed to fetch draws');
        }
    };

    // Discounts collection removed from portal (moved to Perks page)

    const fetchLoyaltyBalance = async () => {
        try {
            if (!SecureStorage.isCustomerTokenValid()) {
                console.log('No valid customer token found');
                setLoyaltyBalance({
                    totalPoints: 0,
                    availablePoints: 0,
                    redeemedPoints: 0
                });
                return;
            }
            
            console.log('Fetching loyalty balance with secure authentication');
            const balance = await getLoyaltyBalance();
            console.log('Loyalty balance response:', balance);
            setLoyaltyBalance(balance);
        } catch (error: any) {
            console.error('Error fetching loyalty balance:', error);
            
            // If session expired, show message to user
            if (error.message.includes('Session expired')) {
                console.warn('Customer session expired, redirecting to sign in');
                // You might want to redirect to customer sign in here
            }
            
            // Always show default values if fetch fails
            setLoyaltyBalance({
                totalPoints: 0,
                availablePoints: 0,
                redeemedPoints: 0
            });
        }
    };

    const formatGeneratedCode = (code: string) => {
        if (!code) return '';
        // If already spaced, keep it
        if (code.includes(' ')) return code;
        const pairs = code.match(/.{1,2}/g);
        return pairs ? pairs.join(' ') : code;
    };

    // Map drawId -> draw name for quick lookup in Tickets tab labels
    const drawNameById = useMemo(() => {
        const map = new Map<string, string>();
        (draws.upcoming || []).forEach((d) => {
            if (d?.id) map.set(d.id, d.name);
        });
        return map;
    }, [draws.upcoming]);

    const DrawCard = ({ draw, showCopyLink = true }: { draw: Draw; showCopyLink?: boolean }) => {
        const [copySuccess, setCopySuccess] = useState(false);

        const handleCopyLink = async (e: React.MouseEvent) => {
            e.stopPropagation();
            const url = `${window.location.origin}/draw-info/${draw.id}`;
            try {
                await navigator.clipboard.writeText(url);
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2000);
            } catch (err) {
                console.error('Failed to copy: ', err);
            }
        };

        return (
            <div className="bg-white rounded-lg p-6 flex items-center justify-between border border-gray-200 hover:shadow-sm transition-all duration-200">
                {/* Left: Calendar Icon */}
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </div>

                    {/* Draw Info */}
                    <div className='text-left'>
                        <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold text-gray-900">{draw.name}</h3>
                            {showCopyLink && (
                                <button
                                    type="button"
                                    onClick={handleCopyLink}
                                    className={`text-xs font-medium focus:outline-none transition-colors ${
                                        copySuccess 
                                            ? 'text-green-600' 
                                            : 'text-blue-600 hover:text-blue-800'
                                    }`}
                                    title="Copy draw link"
                                >
                                    {copySuccess ? 'Copied!' : 'Copy Link'}
                                </button>
                            )}
                        </div>
                        <p className="text-sm text-gray-600">
                            Prize: {draw.prize} {draw.currency}
                        </p>
                        <p className="text-sm text-gray-600">
                            Entry: {draw.entryCost} {draw.currency}
                        </p>
                        <p className="text-xs text-gray-400 font-mono mt-1">
                            ID: {draw.id.slice(0, 15)}...
                        </p>
                    </div>
                </div>

                {/* Right: Run Date & Participants */}
                <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                        {format(new Date(draw.runAt), 'MMM dd, yyyy')}
                    </p>
                    <p className="text-xs text-gray-500">
                        {format(new Date(draw.runAt), 'h:mm a')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                        {draw.noOfPartic} participants
                    </p>
                </div>
            </div>
        );
    };

    const TicketCard = ({ ticket, type, showStatus = false }: { ticket: Ticket; type: string; showStatus?: boolean }) => {
        const [copySuccess, setCopySuccess] = useState(false);

        useEffect(() => {
            if (ticket.hasWon) {
                confetti({
                    particleCount: 100,
                    spread: 70,
                    origin: { y: 0.6 }
                });
            }
        }, [ticket.hasWon]);

        const handleCopyMembershipLink = async (e: React.MouseEvent) => {
            e.stopPropagation();
            const url = `${window.location.origin}/package-info/${ticket.membershipTireId}`;
            try {
                await navigator.clipboard.writeText(url);
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2000);
            } catch (err) {
                console.error('Failed to copy: ', err);
            }
        };

        // Determine card styling based on win status when showStatus is true
        const cardClassName = showStatus 
            ? `rounded-lg p-6 flex items-center justify-between border transition-all duration-200 ${
                ticket.hasWon 
                    ? 'bg-green-50 border-green-200 hover:shadow-green-100' 
                    : 'bg-gray-50 border-gray-200 hover:shadow-gray-100'
              }`
            : 'bg-white rounded-lg p-6 flex items-center justify-between border border-gray-200 hover:shadow-sm transition-all duration-200';

        return (
            <div className={cardClassName}>
                {/* Left: Calendar Icon */}
                <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        showStatus 
                            ? (ticket.hasWon ? 'bg-green-600' : 'bg-gray-500')
                            : 'bg-gray-900'
                    }`}>
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </div>
                    
                    {/* Center: Ticket Info */}
                    <div className='text-left'>
                        <h3 className="text-lg font-bold text-gray-900 tracking-wider mb-1">
                            {formatGeneratedCode(ticket.generatedCode)}
                        </h3>
                        {type === 'membership' ? (
                            <div className="flex items-center gap-2">
                                <p className="text-sm text-gray-600">
                                    {ticket.tier ? `${ticket.tier.name} - ${ticket.tier.frequency}` : 'Membership'}
                                </p>
                                <button
                                    type="button"
                                    onClick={handleCopyMembershipLink}
                                    className={`text-xs font-medium focus:outline-none transition-colors ${
                                        copySuccess 
                                            ? 'text-green-600' 
                                            : 'text-blue-600 hover:text-blue-800'
                                    }`}
                                    title="Copy membership link"
                                >
                                    {copySuccess ? 'Copied!' : 'Copy Link'}
                                </button>
                            </div>
                        ) : (
                            <p className="text-sm text-gray-600">
                                {`Draw Ticket — ${drawNameById.get(ticket.drawID) || ''}`}
                            </p>
                        )}
                        {type === 'membership' && ticket.tier && (
                            <div className="mt-1 text-xs text-gray-500">
                                <p>Price: {ticket.tier.price} {ticket.tier.currency}</p>
                                {ticket.leftRecurrence !== undefined && (
                                    <p>Remaining: {ticket.leftRecurrence} cycles</p>
                                )}
                            </div>
                        )}
                        {type === 'draw' && ticket.ticketBreakdown && (
                            <div className="mt-1 text-xs text-gray-500">
                                <p>Price: {ticket.paymentAmount || 0} {ticket.paymentCurrency || 'USD'}</p>
                                <p>Purchased: {ticket.purchaseDate ? format(new Date(ticket.purchaseDate), 'dd MMM yyyy') : 'Unknown'}</p>
                                <div className="flex gap-2 mt-1">
                                    {ticket.ticketBreakdown.bought > 0 && (
                                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                            {ticket.ticketBreakdown.bought} bought
                                        </span>
                                    )}
                                    {ticket.ticketBreakdown.promotional > 0 && (
                                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                                            {ticket.ticketBreakdown.promotional} promo
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                        {showStatus && (
                            <div className="mt-2">
                                <span
                                    className={`inline-flex items-center px-3 py-1 rounded text-sm font-semibold ${
                                        ticket.hasWon 
                                            ? 'bg-green-100 text-green-800 border border-green-200' 
                                            : 'bg-gray-100 text-gray-600 border border-gray-200'
                                    }`}
                                >
                                    <span
                                        className={`mr-2 h-2 w-2 rounded ${
                                            ticket.hasWon ? 'bg-green-500' : 'bg-gray-400'
                                        }`}
                                    />
                                    {ticket.hasWon ? '🎉 Won!' : 'Not won'}
                                </span>
                            </div>
                        )}
                        <p className="text-xs text-gray-400 font-mono mt-1">
                            ID: {ticket.drawID.slice(0, 15)}...
                        </p>
                    </div>
                </div>

                {/* Right: Run Date & Cancel Button */}
                <div className="flex items-center gap-6">
                    {ticket.runDate && (
                        <div className="text-right">
                            <div className="flex items-center gap-1 text-gray-500 mb-1">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="text-xs">Run Date</span>
                            </div>
                            <p className="text-sm font-medium text-gray-900">
                                {format(new Date(ticket.runDate), 'dd MMM yyyy HH:mm')}
                            </p>
                        </div>
                    )}
                    
                    {type === 'membership' && (
                        <>
                            {ticket.paymentStatus === 'REFUNDED' || ticket.cancelledAt ? (
                                <div className="bg-gray-100 text-gray-600 px-4 py-2 rounded-sm text-sm font-medium">
                                    {ticket.cancelledAt ? `Cancelled on ${format(new Date(ticket.cancelledAt), 'MMM dd, yyyy')}` : 'Cancelled'}
                                </div>
                            ) : (
                                <button
                                    onClick={() => {
                                        if (window.confirm('Are you sure you want to cancel this membership? This action cannot be undone.')) {
                                            navigate(`/refund?paymentId=${ticket.paymentId}&email=${customerEmail || ''}`);
                                        }
                                    }}
                                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-sm text-sm font-medium transition-colors"
                                >
                                    Cancel Membership
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <span className="loading loading-dots loading-lg"></span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="text-center">
                    <p className="text-red-500 mb-4">{error}</p>
                    <button 
                        onClick={() => navigate(PATHS.SIGNIN)}
                        className="btn btn-primary">
                        Back to Sign In
                    </button>
                </div>
            </div>
        );
    }

    // Note: regularDraws removed - now using draws.upcoming and draws.past

    return (
        <div className="customer-portal bg-gray-50 min-h-screen">
            {/* Top Navigation Header */}
            <CustomerTopNav />

            {/* Main Content */}
            <div className="p-6">
                <div className="w-full">
                    {/* Dark Loyalty Points Section */}
                    <div className="mb-8">
                        {loyaltyBalance && (
                            <div className="loyalty-card">
                                {/* Premium Member Badge */}
                                <div className="loyalty-badge">
                                    <div className="loyalty-badge-dot"></div>
                                    <span className="loyalty-badge-text">PREMIUM MEMBER</span>
                                </div>

                                {/* Title */}
                                <div className="mb-16 text-left relative">
                                    <h1 className="loyalty-title">Loyalty Points</h1>
                                    <p className="loyalty-subtitle">Premium • Exclusive • Unlimited</p>

                                    {/* Trophy Icon */}
                                    <div className="absolute top-6 right-6">
                                        <div className="loyalty-stat-icon bg-opacity-20 bg-gray-500 w-12 h-12 rounded-xl flex items-center justify-center relative">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-trophy w-6 h-6 text-gray-400">
                                                <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
                                                <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
                                                <path d="M4 22h16"></path>
                                                <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
                                                <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
                                                <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
                                            </svg>
                                            <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full" style={{backgroundColor: 'hsl(var(--green-accent))'}}></div>
                                        </div>
                                    </div>
                                </div>

                                {/* Stats Cards - Horizontal Layout */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                                    {/* Total Earned */}
                                    <div className="loyalty-stat-card flex flex-col justify-between p-6 h-40 bg-gray-800 rounded-lg">
                                        <div className="flex justify-between items-start">
                                            <div className="loyalty-stat-icon bg-opacity-20 bg-gray-500">
                                                <svg className="w-5 h-5 text-green-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                                </svg>
                                            </div>
                                            <div className="w-2 h-2 rounded-full bg-gray-600"></div>
                                        </div>
                                        <div className="text-left">
                                            <div className="loyalty-stat-value text-4xl text-white">{loyaltyBalance.totalPoints.toLocaleString()}</div>
                                            <div className="loyalty-stat-label text-white">TOTAL EARNED</div>
                                            </div>
                                    </div>

                                    {/* Available Now */}
                                    <div className="loyalty-stat-card flex flex-col justify-between p-6 h-40 bg-gray-800 rounded-lg">
                                        <div className="flex justify-between items-start">
                                            <div className="loyalty-stat-icon bg-opacity-20 bg-gray-500">
                                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                                    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
                                                    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
                                                    <path d="M4 22h16"></path>
                                                    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
                                                    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
                                                    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
                                                </svg>
                                            </div>
                                            <div className="w-2 h-2 rounded-full bg-gray-600"></div>
                                        </div>
                                        <div className="text-left">
                                            <div className="loyalty-stat-value text-4xl text-white">{loyaltyBalance.availablePoints.toLocaleString()}</div>
                                            <div className="loyalty-stat-label text-white">AVAILABLE NOW</div>
                                            </div>
                                    </div>

                                    {/* Redeemed */}
                                    <div className="loyalty-stat-card flex flex-col justify-between p-6 h-40 bg-gray-800 rounded-lg">
                                        <div className="flex justify-between items-start">
                                            <div className="loyalty-stat-icon bg-opacity-20 bg-gray-500">
                                                <svg className="w-5 h-5 text-yellow-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                                                </svg>
                                            </div>
                                            <div className="w-2 h-2 rounded-full bg-gray-600"></div>
                                        </div>
                                        <div className="text-left">
                                            <div className="loyalty-stat-value text-4xl text-white">{loyaltyBalance.redeemedPoints.toLocaleString()}</div>
                                            <div className="loyalty-stat-label text-white">REDEEMED</div>
                                            </div>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex justify-start gap-4">
                                    <button
                                        onClick={() => navigate(PATHS.REWARDS_CATALOGUE)}
                                        className="loyalty-button-primary rounded-lg"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                                        </svg>
                                        Explore Rewards
                                    </button>
                                    <button
                                        onClick={() => navigate(PATHS.POINTS_HISTORY)}
                                        className="loyalty-button-secondary rounded-lg"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        Points History
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Tabs */}
                <div className="w-full">
                    <div className="flex ml-0">
                        <div className="mb-8 p-1 bg-gray-100 rounded-lg grid grid-cols-5 gap-1 w-full">
                        <button
                                className={`w-full px-6 py-2 text-sm font-semibold rounded-sm text-center transition-colors duration-300 ${activeTab === 'tickets'
                                    ? 'bg-gray-900 text-white'
                                        : 'text-gray-600 hover:bg-gray-200'
                            }`}
                            onClick={() => setActiveTab('tickets')}
                        >
                            Tickets ({tickets.upcomingTickets.length})
                        </button>
                        <button
                                className={`w-full px-6 py-2 text-sm font-semibold rounded-sm text-center transition-colors duration-300 ${activeTab === 'memberships'
                                    ? 'bg-gray-900 text-white'
                                        : 'text-gray-600 hover:bg-gray-200'
                            }`}
                            onClick={() => setActiveTab('memberships')}
                        >
                            Memberships ({tickets.memberships.length})
                        </button>
                        <button
                                className={`w-full px-6 py-2 text-sm font-semibold rounded-sm text-center transition-colors duration-300 ${activeTab === 'draws'
                                    ? 'bg-gray-900 text-white'
                                        : 'text-gray-600 hover:bg-gray-200'
                            }`}
                            onClick={() => setActiveTab('draws')}
                        >
                                Draws ({draws.upcoming.length})
                        </button>
                        <button
                                className={`w-full px-6 py-2 text-sm font-semibold rounded-sm text-center transition-colors duration-300 ${activeTab === 'past'
                                    ? 'bg-gray-900 text-white'
                                        : 'text-gray-600 hover:bg-gray-200'
                            }`}
                            onClick={() => setActiveTab('past')}
                        >
                                Past Draws ({draws.past.length})
                        </button>
                        <button
                                className={`w-full px-6 py-2 text-sm font-semibold rounded-sm text-center transition-colors duration-300 ${activeTab === 'pastTickets'
                                        ? 'bg-gray-900 text-white'
                                        : 'text-gray-600 hover:bg-gray-200'
                                    }`}
                                onClick={() => setActiveTab('pastTickets')}
                            >
                                Past Tickets ({tickets.pastTickets.length})
                        </button>
                        </div>
                    </div>

                {/* Tab Content */}
                <div className="min-h-96">
                {activeTab === 'tickets' && (
                    <div>
                        {tickets.upcomingTickets.length > 0 ? (
                            <div className="space-y-6">
                                {Array.from(
                                    tickets.upcomingTickets.reduce((map, t) => {
                                        const title = drawNameById.get(t.drawID) || 'Draw Ticket';
                                        if (!map.has(title)) map.set(title, [] as Ticket[]);
                                        map.get(title)!.push(t);
                                        return map;
                                    }, new Map<string, Ticket[]>()
                                )).map(([title, groupTickets]) => (
                                    <div key={title} className="bg-white rounded-lg border border-gray-200">
                                        {/* Group Header */}
                                        <button
                                            type="button"
                                            className="w-full flex items-center justify-between px-5 py-4 border-b bg-gray-100"
                                            onClick={() => setOpenTicketGroups((prev) => ({ ...prev, [title]: !(prev[title] ?? true) }))}
                                        >
                                            <div className="flex items-center gap-3 text-left">
                                                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                                                    <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <div className="text-base font-semibold text-gray-900">{title}</div>
                                                    <div className="text-xs text-gray-500">{groupTickets.length} {groupTickets.length === 1 ? 'ticket' : 'tickets'}</div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="px-3 py-1 rounded-full text-xs bg-gray-100 text-gray-700">{groupTickets.length}</div>
                                                <svg
                                                    className={`w-4 h-4 text-gray-500 transition-transform ${openTicketGroups[title] ?? true ? 'rotate-180' : ''}`}
                                                    fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </div>
                                        </button>
                                        {/* Group Items */}
                                        {(openTicketGroups[title] ?? true) && (
                                        <div className="">
                                            {Array.from(
                                                groupTickets.reduce((pm, t) => {
                                                    const key = t.paymentId || (t.purchaseDate ? String(t.purchaseDate) : `noPay-${t.id}`);
                                                    if (!pm.has(key)) pm.set(key, [] as Ticket[]);
                                                    pm.get(key)!.push(t);
                                                    return pm;
                                                }, new Map<string, Ticket[]>())
                                            ).map(([purchaseKey, purchaseTickets], idx) => (
                                                <div
                                                    key={purchaseKey}
                                                    className={`${idx % 2 === 0 ? 'bg-gray-50' : 'bg-blue-100'} divide-y rounded-md my-2 mx-3 border border-gray-200 border-l-4 ${idx % 2 === 0 ? 'border-l-violet-200' : 'border-l-blue-200'}`}
                                                >
                                                    {purchaseTickets.map((ticket) => (
                                                <div key={ticket.id} className="flex items-center justify-between px-5 py-4">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-12 h-12 rounded-lg bg-gray-900 flex items-center justify-center">
                                                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                            </svg>
                                                        </div>
                                                        <div className="text-left">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                {/* badges BEFORE code */}
                                                                {ticket.ticketBreakdown?.bought ? (
                                                                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">{ticket.ticketBreakdown.bought} bought</span>
                                                                ) : null}
                                                                {ticket.ticketBreakdown?.promotional ? (
                                                                    <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">{ticket.ticketBreakdown.promotional} promo</span>
                                                                ) : null}
                                                                <div className="text-lg font-bold text-gray-900 tracking-wider font-mono">{formatGeneratedCode(ticket.generatedCode)}</div>
                                                            </div>
                                                            <div className="text-xs text-gray-600 flex items-center gap-4">
                                                                <span>$ Price: {ticket.paymentAmount || 0} {ticket.paymentCurrency || 'AUD'}</span>
                                                                <span>Purchased: {ticket.purchaseDate ? format(new Date(ticket.purchaseDate), 'dd MMM yyyy') : 'Unknown'}</span>
                                                            </div>
                                                            <div className="text-xs text-gray-400 mt-1">ID: {ticket.id.slice(0, 8)}...</div>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-sm font-medium text-gray-900">Run Date</div>
                                                        <div className="text-xs text-gray-600">{ticket.runDate ? format(new Date(ticket.runDate), 'dd MMM yyyy HH:mm') : '-'}</div>
                                                    </div>
                                                </div>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <p className="text-gray-500">No current tickets</p>
                            </div>
                        )}
                    </div>
                )}
                {activeTab === 'memberships' && (
                    <div>
                        {tickets.memberships.length > 0 ? (
                            <div className="space-y-4">
                                        {tickets.memberships
                                            .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                                            .map((ticket) => (
                                    <TicketCard key={ticket.id} ticket={ticket} type="membership" />
                                ))}
                                {tickets.memberships.length > itemsPerPage && (
                                    <div className="flex justify-center mt-8">
                                        <div className="flex gap-2">
                                            {Array.from({ length: Math.ceil(tickets.memberships.length / itemsPerPage) }, (_, i) => (
                                                <button 
                                                    key={i + 1}
                                                            className={`w-10 h-10 rounded-full text-sm font-medium transition-all ${currentPage === i + 1
                                                            ? 'bg-black text-white' 
                                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                    }`}
                                                    onClick={() => setCurrentPage(i + 1)}
                                                >
                                                    {i + 1}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <p className="text-gray-500">No active memberships</p>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'draws' && (
                    <div>
                                {draws.upcoming.length > 0 ? (
                            <div className="space-y-4">
                                        {draws.upcoming.slice(
                                    (currentPage - 1) * itemsPerPage,
                                    currentPage * itemsPerPage
                                        ).map(draw => (
                                            <DrawCard key={draw.id} draw={draw} />
                                ))}
                                        {draws.upcoming.length > itemsPerPage && (
                                    <div className="flex justify-center mt-8">
                                        <div className="flex gap-2">
                                                    {Array.from({ length: Math.ceil(draws.upcoming.length / itemsPerPage) }, (_, i) => (
                                                <button 
                                                    key={i + 1}
                                                            className={`w-10 h-10 rounded-full text-sm font-medium transition-all ${currentPage === i + 1
                                                            ? 'bg-black text-white' 
                                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                    }`}
                                                    onClick={() => setCurrentPage(i + 1)}
                                                >
                                                    {i + 1}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                        <p className="text-gray-500">No upcoming draws available</p>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'past' && (
                    <div>
                                {draws.past.length > 0 ? (
                            <div className="space-y-4">
                                        {draws.past.slice(
                                    (currentPage - 1) * itemsPerPage,
                                    currentPage * itemsPerPage
                                        ).map(draw => (
                                            <DrawCard key={draw.id} draw={draw} showCopyLink={false} />
                                ))}
                                        {draws.past.length > itemsPerPage && (
                                    <div className="flex justify-center mt-8">
                                        <div className="flex gap-2">
                                                    {Array.from({ length: Math.ceil(draws.past.length / itemsPerPage) }, (_, i) => (
                                                <button 
                                                    key={i + 1}
                                                            className={`w-10 h-10 rounded-full text-sm font-medium transition-all ${currentPage === i + 1
                                                            ? 'bg-black text-white' 
                                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                    }`}
                                                    onClick={() => setCurrentPage(i + 1)}
                                                >
                                                    {i + 1}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <p className="text-gray-500">No past draws available</p>
                            </div>
                        )}
                    </div>
                )}
                        {activeTab === 'pastTickets' && (
                            <div>
                                {tickets.pastTickets.length > 0 ? (
                                    <div className="space-y-4">
                                        {tickets.pastTickets.slice(
                                            (currentPage - 1) * itemsPerPage,
                                            currentPage * itemsPerPage
                                        ).map(ticket => (
                                            <TicketCard key={ticket.id} ticket={ticket} type="draw" showStatus={true} />
                                        ))}
                                        {tickets.pastTickets.length > itemsPerPage && (
                                            <div className="flex justify-center mt-8">
                                                <div className="flex gap-2">
                                                    {Array.from({ length: Math.ceil(tickets.pastTickets.length / itemsPerPage) }, (_, i) => (
                                                        <button
                                                            key={i + 1}
                                                            className={`w-10 h-10 rounded-full text-sm font-medium transition-all ${currentPage === i + 1
                                                                    ? 'bg-black text-white'
                                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                                }`}
                                                            onClick={() => setCurrentPage(i + 1)}
                                                        >
                                                            {i + 1}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-center py-12">
                                        <p className="text-gray-500">No past tickets available</p>
                                    </div>
                                )}
                            </div>
                        )}
            </div>
            {/* Discounts content removed - moved to Perks page */}
            </div>
            </div>
            <CustomerFooter />
        </div>
    );
};

// const styles = `
// .filter-gold {
//     filter: invert(70%) sepia(80%) saturate(1000%) hue-rotate(359deg) brightness(105%) contrast(105%);
// `;

export default CustomerPortal;